import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  POS_UI_PREFERENCES_KEY,
  cloneShortcutDefinition,
  cloneShortcutMap,
  createDefaultPosUiPreferences,
  type PosShortcutAction,
  type PosShortcutDefinition,
  type PosShortcutMap,
  type PosUiPreferences,
} from '../state/types';
import { loadPreferences, persistPreferences } from '../state/persistence';

export interface UsePosUiState {
  getLastVariantForItem: (itemId: number) => number | null;
  setLastVariantForItem: (itemId: number, variantId: number | null) => void;
  getPreferredProfessionalForItem: (itemId: number) => number | null;
  setPreferredProfessionalForItem: (itemId: number, professionalId: number | null) => void;
  isCustomerPanelCollapsed: boolean;
  setCustomerPanelCollapsed: (collapsed: boolean) => void;
  isDiscountsPanelCollapsed: boolean;
  setDiscountsPanelCollapsed: (collapsed: boolean) => void;
  isPaymentsPanelCollapsed: boolean;
  setPaymentsPanelCollapsed: (collapsed: boolean) => void;
  isShippingPanelCollapsed: boolean;
  setShippingPanelCollapsed: (collapsed: boolean) => void;
  preferredPaymentMethodId: number | null;
  setPreferredPaymentMethodId: (methodId: number | null) => void;
  lastCustomerId: number | null;
  setLastCustomerId: (customerId: number | null) => void;
  bottomBarPreferences: PosUiPreferences['bottomBar'];
  setBottomBarPreferences: (preferences: Partial<PosUiPreferences['bottomBar']>) => void;
  layoutMode: PosUiPreferences['layout']['mobileMode'];
  setLayoutMode: (mode: PosUiPreferences['layout']['mobileMode']) => void;
  orderNoteDraft: string;
  setOrderNoteDraft: (draft: string) => void;
  shortcuts: PosShortcutMap;
  setShortcutsForAction: (action: PosShortcutAction, shortcuts: PosShortcutDefinition[]) => void;
  resetShortcutsForAction: (action: PosShortcutAction) => void;
  restoreDefaults: () => void;
}

const sanitizeShortcutDefinition = (definition: PosShortcutDefinition): PosShortcutDefinition => {
  const key = definition.key?.trim();
  if (!key) {
    throw new Error('Shortcut key no puede ser vacío.');
  }

  const normalizedKey = key.toLowerCase();
  const sanitized: PosShortcutDefinition = { key: normalizedKey };

  if (definition.system) {
    sanitized.system = true;
  }
  if (definition.shift) {
    sanitized.shift = true;
  }
  if (definition.alt) {
    sanitized.alt = true;
  }
  if (definition.ctrl) {
    sanitized.ctrl = true;
  }
  if (definition.meta) {
    sanitized.meta = true;
  }

  return sanitized;
};

const dedupeShortcutDefinitions = (definitions: PosShortcutDefinition[]): PosShortcutDefinition[] => {
  const seen = new Set<string>();
  const result: PosShortcutDefinition[] = [];

  definitions.forEach((definition) => {
    const serialized = JSON.stringify(definition);
    if (seen.has(serialized)) {
      return;
    }
    seen.add(serialized);
    result.push(cloneShortcutDefinition(definition));
  });

  return result;
};

export const usePosUiState = (): UsePosUiState => {
  const [preferences, setPreferences] = useState<PosUiPreferences>(() => loadPreferences());

  const applyPreferences = useCallback(
    (updater: (current: PosUiPreferences) => PosUiPreferences) => {
      setPreferences((current) => {
        const next = updater(current);
        persistPreferences(next);
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== POS_UI_PREFERENCES_KEY) {
        return;
      }
      setPreferences(loadPreferences());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const getLastVariantForItem = useCallback(
    (itemId: number) => preferences.lastVariantByItemId[itemId] ?? null,
    [preferences.lastVariantByItemId],
  );

  const setLastVariantForItem = useCallback(
    (itemId: number, variantId: number | null) => {
      applyPreferences((current) => ({
        ...current,
        lastVariantByItemId: {
          ...current.lastVariantByItemId,
          [itemId]: variantId,
        },
      }));
    },
    [applyPreferences],
  );

  const getPreferredProfessionalForItem = useCallback(
    (itemId: number) => preferences.lastProfessionalByItemId[itemId] ?? null,
    [preferences.lastProfessionalByItemId],
  );

  const setPreferredProfessionalForItem = useCallback(
    (itemId: number, professionalId: number | null) => {
      applyPreferences((current) => ({
        ...current,
        lastProfessionalByItemId: {
          ...current.lastProfessionalByItemId,
          [itemId]: professionalId,
        },
      }));
    },
    [applyPreferences],
  );

  const setCustomerPanelCollapsed = useCallback(
    (collapsed: boolean) => {
      applyPreferences((current) => ({
        ...current,
        collapsed: {
          ...current.collapsed,
          customer: collapsed,
        },
      }));
    },
    [applyPreferences],
  );

  const setDiscountsPanelCollapsed = useCallback(
    (collapsed: boolean) => {
      applyPreferences((current) => ({
        ...current,
        collapsed: {
          ...current.collapsed,
          discounts: collapsed,
        },
      }));
    },
    [applyPreferences],
  );

  const setPaymentsPanelCollapsed = useCallback(
    (collapsed: boolean) => {
      applyPreferences((current) => ({
        ...current,
        collapsed: {
          ...current.collapsed,
          payments: collapsed,
        },
      }));
    },
    [applyPreferences],
  );

  const setShippingPanelCollapsed = useCallback(
    (collapsed: boolean) => {
      applyPreferences((current) => ({
        ...current,
        collapsed: {
          ...current.collapsed,
          shipping: collapsed,
        },
      }));
    },
    [applyPreferences],
  );

  const setPreferredPaymentMethodId = useCallback(
    (methodId: number | null) => {
      applyPreferences((current) => ({
        ...current,
        payments: {
          ...current.payments,
          preferredMethodId: methodId,
        },
      }));
    },
    [applyPreferences],
  );

  const setLastCustomerId = useCallback(
    (customerId: number | null) => {
      applyPreferences((current) => ({
        ...current,
        lastCustomerId: customerId,
      }));
    },
    [applyPreferences],
  );

  const setBottomBarPreferences = useCallback(
    (nextPreferences: Partial<PosUiPreferences['bottomBar']>) => {
      applyPreferences((current) => ({
        ...current,
        bottomBar: {
          ...current.bottomBar,
          ...nextPreferences,
        },
      }));
    },
    [applyPreferences],
  );

  const setLayoutMode = useCallback(
    (mode: PosUiPreferences['layout']['mobileMode']) => {
      applyPreferences((current) => ({
        ...current,
        layout: {
          ...current.layout,
          mobileMode: mode,
        },
      }));
    },
    [applyPreferences],
  );

  const setOrderNoteDraft = useCallback(
    (draft: string) => {
      applyPreferences((current) => ({
        ...current,
        orderNoteDraft: draft,
      }));
    },
    [applyPreferences],
  );

  const setShortcutsForAction = useCallback(
    (action: PosShortcutAction, shortcuts: PosShortcutDefinition[]) => {
      applyPreferences((current) => {
        let sanitized: PosShortcutDefinition[] = [];
        try {
          sanitized = shortcuts.map(sanitizeShortcutDefinition);
        } catch (error) {
          sanitized = [];
        }
        const deduped = dedupeShortcutDefinitions(sanitized);
        const nextShortcuts = deduped.length > 0
          ? deduped
          : cloneShortcutMap(createDefaultPosUiPreferences().shortcuts)[action];
        return {
          ...current,
          shortcuts: {
            ...current.shortcuts,
            [action]: nextShortcuts.map((definition) => cloneShortcutDefinition(definition)),
          },
        };
      });
    },
    [applyPreferences],
  );

  const resetShortcutsForAction = useCallback(
    (action: PosShortcutAction) => {
      const defaults = createDefaultPosUiPreferences();
      setShortcutsForAction(action, defaults.shortcuts[action]);
    },
    [setShortcutsForAction],
  );

  const restoreDefaults = useCallback(() => {
    const defaults = createDefaultPosUiPreferences();
    persistPreferences(defaults);
    setPreferences(defaults);
  }, []);

  const shortcuts = useMemo(() => cloneShortcutMap(preferences.shortcuts), [preferences.shortcuts]);

  return {
    getLastVariantForItem,
    setLastVariantForItem,
    getPreferredProfessionalForItem,
    setPreferredProfessionalForItem,
    isCustomerPanelCollapsed: preferences.collapsed.customer,
    setCustomerPanelCollapsed,
    isDiscountsPanelCollapsed: preferences.collapsed.discounts,
    setDiscountsPanelCollapsed,
    isPaymentsPanelCollapsed: preferences.collapsed.payments,
    setPaymentsPanelCollapsed,
    isShippingPanelCollapsed: preferences.collapsed.shipping,
    setShippingPanelCollapsed,
    preferredPaymentMethodId: preferences.payments.preferredMethodId,
    setPreferredPaymentMethodId,
    lastCustomerId: preferences.lastCustomerId,
    setLastCustomerId,
    bottomBarPreferences: preferences.bottomBar,
    setBottomBarPreferences,
    layoutMode: preferences.layout.mobileMode,
    setLayoutMode,
    orderNoteDraft: preferences.orderNoteDraft,
    setOrderNoteDraft,
    shortcuts,
    setShortcutsForAction,
    resetShortcutsForAction,
    restoreDefaults,
  };
};

export default usePosUiState;
